import { ICustomerCaseData } from 'types/customer-case';

import CentrePointIcon from 'assets/customer-cases/centre-point.svg';
import StretchToolIcon from 'assets/customer-cases/stretch-tool.svg';
import { colors } from 'styles';
import Typography from '@mui/material/Typography/Typography';

import BiggerBlueShipImage from 'assets/customer-cases/blue-ship.webp';

import DozenBoatsImage from 'assets/customer-cases/brunswick-use-case/dozen-boats.webp';
import ThreeBoatsImage from 'assets/customer-cases/brunswick-use-case/three-boats.webp';
import FiveBoatsImage from 'assets/customer-cases/brunswick-use-case/five-boats.webp';
import CityViewImage from 'assets/customer-cases/brunswick-use-case/city-view.webp';

import WhiteFishingBoatIcon from 'assets/industries/fishing-boat.svg';
import WhiteSimulationIcon from 'assets/customer-cases/simulation.svg';
import WhiteAgileIcon from 'assets/white-agile.svg';
import WhiteCircleAroundIcon from 'assets/white-circle-around.svg';
import WhiteCoinInHandImage from 'assets/customer-cases/coin-in-hand.svg';
import WhiteArrowUpIcon from 'assets/white-arrow-up.svg';
import WhiteAutomationIcon from 'assets/white-automation.svg';
import HeroImage from 'assets/applications/app-1.webp';
import { intelligentSystemTestingApplicationMeta } from 'constants/pages';

export const thirdCustomerCaseData: ICustomerCaseData = {
  heroData: {
    heroTitle: 'Develop & Test AI Auto-Docking for Leisure Boats',
    heroDescription: `
      Learn how a maritime OEM used AILiveSim to develop, test, and validate an AI-powered auto-docking system 
      for leisure boats in complex and unpredictable maritime conditions. Simulation accelerated iteration, 
      minimized risk, and enabled a faster path to launching the industry’s first intelligent co-captain solution. 
      Discover how smart simulation is transforming the future of autonomous boating.
    `,
    heroListTitle: 'Transforming Leisure Boating with Advanced Simulation Technology',
    buttonLabel: 'Explore Maritime Industry',
    buttonLink: '/Industries/maritime',
    heroListItems: [
      {
        description:
          'A leading maritime OEM used simulation to design, train, and validate its AI co-captain system under realistic docking conditions.',
        Icon: WhiteFishingBoatIcon,
        title: 'Pioneering OEM Validates Intelligent Auto-Docking System with AILiveSim',
      },
      {
        description:
          'Scenario-based simulation replaced costly and risky field trials, enabling fast iteration and reducing time-to-market.',
        Icon: CentrePointIcon,
        title: 'Accelerated Development with Safer, Smarter Testing Workflows',
      },
      {
        description:
          'AILiveSim provided the data-rich environment needed to refine AI performance and safety — without physical prototypes.',
        Icon: StretchToolIcon,
        title: 'Confidence Through Real-World Fidelity and Scalable Validation',
      },
    ],
    heroImages: {
      firstImage: {
        imageSrc: DozenBoatsImage,
        altText: 'Dozen boats in the water near the shore',
      },
      secondImage: {
        imageSrc: ThreeBoatsImage,
        altText: 'Three boats in the water near the shore',
      },
      thirdImage: {
        imageSrc: FiveBoatsImage,
        altText: 'Five boats in the water near the shore',
      },
      fourthImage: {
        imageSrc: CityViewImage,
        altText: 'City view with boats in the water',
      },
    },
  },
  customerCase: {
    title: 'The Challenge: Overcoming Testing Barriers in Real Maritime Conditions',
    description: (
      <Typography fontSize="18px" color={colors.mainDark}>
        The customer set out to revolutionize leisure boating with autonomous docking technology —
        but testing in real maritime environments was slow, expensive, and unpredictable.
      </Typography>
    ),
    bottomText:
      'To innovate at speed, the customer needed a smarter, scalable way to test and refine its AI-driven solutions without relying solely on physical prototypes.',
    image: BiggerBlueShipImage,
    items: [
      'Highly variable weather and sea conditions',
      'Difficulty recreating realistic docking scenarios',
      'Safety risks during early maneuver testing',
      'Limited access to reliable, stress-tested data',
    ],
    link: '',
  },
  firstInformationBlockData: {
    title: 'The Solution: Simulate the Future of Autonomous Docking with AILiveSim',
    description: `
    With AILiveSim, the OEM integrated high-fidelity simulation into its development 
    pipeline to test and refine an AI-powered auto-docking system. The platform enabled 
    realistic, repeatable docking scenarios featuring dynamic winds, waves, and obstacles 
    — helping the team validate performance, accelerate development, and reduce reliance 
    on physical trials.`,
    items: [
      {
        Icon: WhiteSimulationIcon,
        description:
          'Dynamic docking environments: Simulate real-world maneuvers with changing winds, sea states, and complex obstacles.',
        title: 'Scenario-Based Simulation',
      },
      {
        Icon: WhiteAgileIcon,
        description:
          'Reliable KPI tracking: Measure alignment precision, stopping distance, and collision avoidance under varied conditions.',
        title: 'Performance Validation',
      },
      {
        Icon: WhiteCircleAroundIcon,
        description:
          'Faster, risk-free development: Run thousands of safe, repeatable tests to speed up iterations and cut time-to-market.',
        title: 'Accelerated Testing',
      },
      {
        Icon: WhiteCoinInHandImage,
        description:
          'Refine before real-world trials: Reduce development risk and build confidence by validating in simulation first.',
        title: 'Cost-Efficient Innovation',
      },
    ],
    link: '/platform',
    linkLabel: 'Learn how the platform works',
  },
  resultsData: {
    title: 'Results: Accelerated Innovation and Scalable Autonomy',
    description: `
    With AILiveSim’s simulation platform now embedded into the OEM’s development workflows, the customer is positioned to scale intelligent boating solutions faster, expanding to more vessels, broader scenarios, and higher levels of autonomy.
    `,
    listTitle: 'Key Outcomes',
    clientFeedbackMessage: `“Simulation allowed us to iterate quickly, validate safely, and deliver an intelligent co-captain experience the market hadn’t seen before.”`,
    clientLabel: '— Michael Grant, Innovation Lead at a major maritime OEM',
    listItems: [
      <li key="1" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <strong>Integrated intelligent simulation into development workflows:</strong> Enabled
        faster scaling of autonomous boating solutions across more vessels, broader scenarios, and
        higher autonomy levels.
      </li>,
      <li key="2" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <strong>Faster development:</strong> Reduced reliance on physical prototypes
      </li>,
      <li key="3" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <strong>Higher safety:</strong> Risk-free validation of complex docking maneuvers
      </li>,
      <li key="4" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <strong>Greater scalability:</strong> Testing expansion across multiple environments and
        boat models made possible
      </li>,
    ],
    resultTitle: 'Reduce Costs and Risks with Advanced Simulation Technology',
    resultDescription:
      'Simulation allowed the customer to safely refine their AI auto-docking system, cutting costs and boosting confidence before real-world trials.',
  },
  secondInformationBlockData: {
    title: 'Unlocking Scalable Innovation in Leisure Boating',
    description: `AILiveSim empowered the OEM to streamline development, improve safety, and accelerate innovation across autonomous vessel programs. By embedding intelligent simulation into their workflow, the team was able to validate complex systems faster and with greater confidence.`,
    items: [
      {
        Icon: WhiteArrowUpIcon,
        description:
          'Reduced reliance on physical prototyping by enabling early-stage testing in realistic virtual environments.',
        title: 'Faster Development, Fewer Prototypes',
      },
      {
        Icon: WhiteSimulationIcon,
        description:
          'Achieved risk-free validation of complex docking maneuvers, minimizing safety concerns during early development.',
        title: 'Safer Testing from the Start',
      },
      {
        Icon: StretchToolIcon,
        description:
          'Expanded testing across multiple vessel types and environments, laying the groundwork for broader adoption and higher autonomy levels.',
        title: 'Ready to Scale',
      },
      {
        Icon: WhiteAutomationIcon,
        description:
          'Integrated intelligent simulation into the development process to enable faster iteration, better insights, and more efficient scaling of autonomous boating solutions.',
        title: 'Simulation-Driven Workflows',
      },
    ],
  },
  applicationData: {
    bottomText: '',
    description:
      'Train smarter with synthetic data that captures real-world complexity, delivering the diversity and accuracy your AI models need to perform reliably in any environment.',
    image: HeroImage,
    items: [],
    title: 'Learn More About Optimizing the Volume of Tests with Simulation in the Loop',
    link: intelligentSystemTestingApplicationMeta.url,
  },
};
